/**
 * AuthBootstrap — fires once per tab on mount to restore session from cookie.
 *
 * If no access token is present (new tab), calls POST /v1/auth/refresh using
 * the httpOnly refresh cookie. Shows a full-screen spinner while in-flight so
 * RequireAuth doesn't fire a premature redirect to /login before the attempt
 * completes. Renders children after initialization regardless of outcome.
 *
 * BOLT-403: also surfaces the "impersonation ended" toast when this tab was
 * just switched back to the super-admin identity by a silent refresh that
 * found the session over (the switch does a full-page replace, so the toast
 * must be raised on the NEXT mount — this one).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useAuthStore } from '@/store/auth.js';
import { bootstrapSession } from '@/lib/api-client.js';
import { consumeImpersonationEnded } from '@/auth/impersonation.js';
import { useToast } from '@/hooks/useToast.js';

export function AuthBootstrap({ children }: { children: ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [ready, setReady] = useState(Boolean(accessToken));
  const { toast } = useToast();

  useEffect(() => {
    const endedEmail = consumeImpersonationEnded();
    if (endedEmail !== null) {
      toast(
        endedEmail
          ? `Your impersonation session as ${endedEmail} has ended — you're back on your own account.`
          : "Your impersonation session has ended — you're back on your own account.",
        'info',
      );
    }
    if (accessToken) {
      setReady(true);
    } else {
      bootstrapSession().finally(() => setReady(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; accessToken seeds initial state only
  }, []);

  if (!ready) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
}
